import {
  OverlayArrow,
  Tooltip as TooltipAria,
  type TooltipProps as TooltipPropsAria,
  TooltipTrigger,
  type TooltipTriggerComponentProps,
} from "react-aria-components";
import type { ReactNode } from "react";
import { tv } from "tailwind-variants";

import { cn } from "@/lib/helpers/cn";
import { DEFAULT_TOOLTIP_DELAY } from "@/lib/constants/delay";

import { Button2, type ButtonProps } from "../atoms/Button";

import "./Tooltip.styles.css";

type TooltipProps = {
  /** When true, renders children directly as the trigger instead of wrapping in Button2.
   * Use when children already contain an interactive element (e.g. Menu2). */
  asChild?: boolean;
  className?: string;
  /** Rendered as the first element from the left, aligned to the top. Switches text alignment to left. */
  icon?: ReactNode;
  text: string;
  tooltipTriggerProps?: Omit<TooltipTriggerComponentProps, "children">;
  triggerProps?: ButtonProps;
} & TooltipPropsAria;

export function Tooltip2(props: TooltipProps) {
  const {
    asChild,
    children,
    className,
    icon,
    text,
    tooltipTriggerProps,
    triggerProps,
    ...tooltipProps
  } = props;
  const { tooltip } = tooltipStyles({ hasIcon: !!icon });

  const trigger = asChild ? (
    children
  ) : (
    <Button2
      // Bootstrap override. Assume if trigger props are passed tooltip is clickable
      className={cn(!triggerProps && "tw:cursor-help!")}
      rawChildren
      variant="blank"
      {...triggerProps}
    >
      {children}
    </Button2>
  );

  return (
    <TooltipTrigger delay={DEFAULT_TOOLTIP_DELAY} {...tooltipTriggerProps}>
      {trigger}

      <TooltipAria className={cn(tooltip(), className)} {...tooltipProps}>
        <OverlayArrow className="react-aria-OverlayArrow">
          <svg fill="currentColor" height={6} viewBox="0 0 12 6" width={12}>
            <path d="M0 0 L6 6 L12 0" />
          </svg>
        </OverlayArrow>

        {icon ? (
          <div className="tw:flex tw:items-start tw:gap-2">
            <span className="tw:shrink-0">{icon}</span>
            <span>{text}</span>
          </div>
        ) : (
          text
        )}
      </TooltipAria>
    </TooltipTrigger>
  );
}

const tooltipStyles = tv({
  defaultVariants: {
    hasIcon: false,
  },
  slots: {
    tooltip: cn(
      "react-aria-Tooltip", // aria builtin
      "tw:rounded-sm tw:bg-fill-navy-primary tw:p-2.5 tw:pb-2",
      "tw:text-sm tw:text-text-white tw:outline-none",
      "tw:max-w-[25ch]",
    ),
  },
  variants: {
    hasIcon: {
      false: { tooltip: "tw:text-center" },
      true: { tooltip: "tw:text-left" },
    },
  },
});
